/**
 * error-toast.ts
 *
 * Tiny pub-sub used to surface backend error responses as a corner toast
 * with the CRSC-XXXX code + Copy button. Decoupled from React so the
 * api.ts fetch wrapper can call it without pulling React into the bundle's
 * data-layer.
 *
 * The actual toast UI lives in <ErrorToastHost />, mounted in app/layout.
 *
 * Why a dedicated module instead of reusing one of the existing local
 * `useState` toasts: those live inside specific pages (workflow editor,
 * service area map, etc.) and don't render on every screen. A render-time
 * crash on Settings would have nothing to surface a toast through. This
 * one is mounted globally and is always available.
 */

export interface BackendErrorEnvelope {
  code?: string | null;
  error_id?: string | null;
  category?: string | null;
  message_for_operator?: string | null;
  request_id?: string | null;
  url?: string | null;
  status?: number;
}

type Listener = (e: BackendErrorEnvelope) => void;

const _listeners = new Set<Listener>();

export function subscribeErrorToast(fn: Listener): () => void {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/**
 * Show a toast for a backend error envelope. Safe to call from anywhere
 * (browser only — no-op on the server). Multiple subscribers are notified
 * in registration order; if no toast host is mounted yet, the call is
 * silently dropped (a missed toast is better than a crash during boot).
 */
export function showErrorToast(e: BackendErrorEnvelope) {
  if (typeof window === 'undefined') return;
  if (!e || (!e.code && !e.error_id && !e.message_for_operator && !e.status)) return;
  for (const fn of _listeners) {
    try { fn(e); } catch { /* a buggy listener mustn't break the others */ }
  }
}
